import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DataDisplayTable } from 'components/data-display';
import { DateToLocal } from 'components/dates';
import { FormDateInput, FormHelperText, FormRadio, FormTextArea } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { toApiDate } from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';

const defaultObject = {};
const defaultArray = [];

// FormDateInput otherwise defaults to only 5 years ahead, which is too narrow for a
// review scheduled further out.
const latestSelectableDate = moment("2100-01-01").toDate();

// The Outcome step of the complete-review wizard: the review type's outcome level
// (when in use), the scheduled/custom next review date, and a free-text summary.
export function OutcomeStep(
    {
        pendingReview = defaultObject,
        reviewOptions = defaultObject,
        outcomeLevelNumber = null,
        outcomeSummary = "",
        customNextReviewDate = null,
        loading = false,
        saving = false,
        outcomeLevelValidationMessage = null,
        onOutcomeLevelChange,
        onOutcomeSummaryChange,
        onCustomNextReviewDateChange
    }
) {

    const outcomeLevels = pendingReview?.outcomeLevels ?? defaultObject;
    const scheduledNextReviewDate = pendingReview?.scheduledNextReviewDate;

    // A next review date in the past is meaningless, so today is the earliest selectable
    // date. Evaluated per render so the bound does not go stale in a long-lived tab.
    const earliestSelectableDate = moment().startOf('day').toDate();

    function getLevelColour(levelNumber) {
        const option = (reviewOptions?.outcomeLevels ?? defaultArray).find((level) => level.number === levelNumber);

        if (option?.colour == null) {
            return "";
        }

        // StatusBlock/the swatch expects the hex value without the leading hash.
        return option.colour.replace("#", "");
    }

    function buildLevelOptions() {
        return (outcomeLevels.levels ?? defaultArray).map((level) => {
            const colour = getLevelColour(level.levelNumber);
            return {
                value: level.levelNumber,
                label: (
                    <span className="form-radio-label-with-swatch">
                        <span
                            className="form-radio-swatch"
                            // The swatch shows the level's configured colour, so it is set per level.
                            style={colour ? { backgroundColor: `#${colour}` } : undefined}
                        />
                        {`Level ${level.levelNumber} - ${level.levelName}`}
                    </span>
                )
            };
        });
    }

    function onLevelChange(event) {
        onOutcomeLevelChange(Number(event.target.value));
    }

    function onCustomDateChange(item) {
        onCustomNextReviewDateChange(toApiDate(item));
    }

    // The Custom Review Date helper text tells the manager what the field does to the driver's
    // schedule. satisfiesScheduledReview describes that effect rather than whether the review is
    // due, so a review that leaves the scheduled one owed says what a date does to it instead of
    // offering a scheduled date to override.
    let customDateHelperText = "Only set this if you don't want to use the scheduled review date.";

    if (pendingReview?.satisfiesScheduledReview === false && pendingReview?.pendingReviewId != null) {
        customDateHelperText = (<>Set a date to move {pendingReview.employee?.name}'s next review to it. Leave blank to leave the scheduled review due <DateToLocal format="DD/MM/YYYY">{pendingReview.dueDate}</DateToLocal> unchanged.</>);
    } else if (scheduledNextReviewDate != null) {
        customDateHelperText = (<>Only set this if you don't want to use the scheduled review date (<DateToLocal format="DD/MM/YYYY">{scheduledNextReviewDate}</DateToLocal>).</>);
    }

    return (
        <div>
            <h2>Outcome</h2>
            <FormHelperText loading={loading}>Summarise the review and set the outcome.</FormHelperText>
            <ShowHide
                evaluator={outcomeLevels.active === true}
                show={(
                    <FormRadio
                        id="outcome-level"
                        headingText="Outcome level"
                        loading={loading}
                        disabled={saving}
                        bordered
                        options={buildLevelOptions()}
                        selectedValue={outcomeLevelNumber}
                        onChange={onLevelChange}
                        dangerHelperText={outcomeLevelValidationMessage}
                    />
                )}
            />
            <ShowHide
                evaluator={pendingReview?.allowCustomNextReviewDate === true}
                show={(
                    <FormDateInput
                        id="outcome-custom-next-review-date"
                        headingText="Custom Review Date"
                        helperText={customDateHelperText}
                        value={customNextReviewDate}
                        minDate={earliestSelectableDate}
                        maxDate={latestSelectableDate}
                        loading={loading}
                        disabled={saving}
                        onChange={onCustomDateChange}
                    />
                )}
                hide={(
                    <ShowHide
                        evaluator={scheduledNextReviewDate != null}
                        show={(
                            <DataDisplayTable
                                loading={loading}
                                displayInRows={false}
                                data={[{
                                    key: "next-review-date",
                                    header: "Next review date",
                                    value: <DateToLocal format="DD/MM/YYYY">{scheduledNextReviewDate}</DateToLocal>
                                }]}
                            />
                        )}
                    />
                )}
            />
            <FormTextArea
                id="outcome-summary"
                headingText="Outcome Summary"
                placeholder="Summarise the outcome of this review..."
                value={outcomeSummary}
                loading={loading}
                disabled={saving}
                fill
                onChange={onOutcomeSummaryChange}
            />
        </div>
    );
}

OutcomeStep.propTypes = {
    pendingReview: PropTypes.object,
    reviewOptions: PropTypes.object,
    outcomeLevelNumber: PropTypes.number,
    outcomeSummary: PropTypes.string,
    customNextReviewDate: PropTypes.string,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    outcomeLevelValidationMessage: PropTypes.string,
    onOutcomeLevelChange: PropTypes.func.isRequired,
    onOutcomeSummaryChange: PropTypes.func.isRequired,
    onCustomNextReviewDateChange: PropTypes.func.isRequired
};
